import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Building2, Calendar, User, Settings, Trash2, Edit, FileText, MoreVertical } from 'lucide-react';
import { useGetVendorsQuery, useCreateVendorMutation, useDeleteVendorMutation, useGetCurrentUserQuery, useRenameVendorMutation } from '../store/api/apiSlice';
import { useSelector } from 'react-redux';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';

interface CreateVendorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; description?: string; vendor_type?: string }) => void;
  isLoading: boolean;
}

const CreateVendorModal: React.FC<CreateVendorModalProps> = ({ isOpen, onClose, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({ name: '', description: '', vendor_type: 'supplier' });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.name.trim()) {
      onSubmit({
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        vendor_type: formData.vendor_type
      });
    }
  };

  const handleClose = () => {
    setFormData({ name: '', description: '', vendor_type: 'supplier' });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
          Create New Vendor
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Vendor Name *
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
              placeholder="Enter vendor name"
              required
              disabled={isLoading}
            />
          </div>
          <div className="mb-4">
            <label htmlFor="vendor_type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Vendor Type
            </label>
            <select
              id="vendor_type"
              value={formData.vendor_type}
              onChange={(e) => setFormData({ ...formData, vendor_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
              disabled={isLoading}
            >
              <option value="supplier">Supplier</option>
              <option value="contractor">Contractor</option>
              <option value="partner">Partner</option>
              <option value="client">Client</option>
              <option value="vendor">Vendor</option>
            </select>
          </div>
          <div className="mb-6">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
              placeholder="Enter vendor description (optional)"
              disabled={isLoading}
            />
          </div>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              disabled={isLoading || !formData.name.trim()}
            >
              {isLoading ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span className="ml-2">Creating...</span>
                </>
              ) : (
                'Create Vendor'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface DeleteConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  vendorName: string;
  isLoading: boolean;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({ isOpen, onClose, onConfirm, vendorName, isLoading }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
        <h2 className="text-xl font-bold text-red-600 dark:text-red-400 mb-4">
          Delete Vendor
        </h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          Are you sure you want to delete "<strong>{vendorName}</strong>"?
        </p>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          This action cannot be undone. All documents associated with this vendor will need to be reassigned.
        </p>
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <LoadingSpinner size="sm" />
                <span className="ml-2">Deleting...</span>
              </>
            ) : (
              'Delete Vendor'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export const VendorsPage: React.FC = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [deleteVendor, setDeleteVendor] = useState<{ id: string; name: string } | null>(null);
  const [vendorTypeFilter, setVendorTypeFilter] = useState<string>('');
  const [renameVendorState, setRenameVendorState] = useState<{ id: string; name: string } | null>(null);
  const navigate = useNavigate();

  // Get auth state to determine when API calls should be made
  const auth = useSelector((state: any) => state.auth);
  const isAuthReady = auth.isAuthenticated && !auth.isLoading && auth.token;

  // API hooks
  const { data: currentUser } = useGetCurrentUserQuery(undefined, {
    skip: !isAuthReady
  });
  const { data: vendorsData, isLoading, error } = useGetVendorsQuery({
    vendor_type: vendorTypeFilter || undefined,
    is_active: true
  }, {
    skip: !isAuthReady
  });
  const [createVendor, { isLoading: isCreating }] = useCreateVendorMutation();
  const [deleteVendorMutation, { isLoading: isDeleting }] = useDeleteVendorMutation();
  const [renameVendorMutation, { isLoading: isRenaming }] = useRenameVendorMutation();

  // Check user permissions
  const canCreateVendors = currentUser?.claims?.some(
    (claim) => claim.claim_type === 'superadmin' || 
               (claim.claim_type === 'tenant_admin' && claim.resource_id === currentUser.tenant_id)
  ) || false;

  const canDeleteVendors = canCreateVendors; // Same permission for delete

  const handleRenameVendor = async () => {
    if (!renameVendorState) return;
    const trimmed = renameVendorState.name.trim();
    if (!trimmed) return;
    try {
      await renameVendorMutation({ id: renameVendorState.id, new_name: trimmed }).unwrap();
      setRenameVendorState(null);
    } catch (error) {
      console.error('Failed to rename vendor:', error);
    }
  };

  const handleCreateVendor = async (data: { name: string; description?: string; vendor_type?: string }) => {
    try {
      await createVendor({
        ...data,
        tenant_id: currentUser!.tenant_id
      }).unwrap();
      setIsCreateModalOpen(false);
    } catch (error) {
      console.error('Failed to create vendor:', error);
      // Handle error (show toast notification, etc.)
    }
  };

  const handleDeleteVendor = async () => {
    if (!deleteVendor) return;
    
    try {
      await deleteVendorMutation(deleteVendor.id).unwrap();
      setDeleteVendor(null);
    } catch (error) {
      console.error('Failed to delete vendor:', error);
      // Handle error (show toast notification, etc.)
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getRiskLevelColor = (riskLevel: string | undefined) => {
    switch (riskLevel) {
      case 'low':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'high':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'critical':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 dark:text-red-400 mb-4">
          Failed to load vendors
        </div>
        <button
          onClick={() => window.location.reload()}
          className="btn-primary"
        >
          Retry
        </button>
      </div>
    );
  }

  const vendors = vendorsData?.items || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            Vendors
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage your contract vendors and suppliers
          </p>
        </div>
        {canCreateVendors && (
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="btn-primary"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Vendor
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex space-x-4">
        <select
          value={vendorTypeFilter}
          onChange={(e) => setVendorTypeFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
        >
          <option value="">All Vendor Types</option>
          <option value="client">Client</option>
          <option value="contractor">Contractor</option>
          <option value="partner">Partner</option>
          <option value="service_provider">Service Provider</option>
          <option value="supplier">Supplier</option>
          <option value="vendor">Vendor</option>
        </select>
      </div>

      {vendors.length === 0 ? (
        <div className="card">
          <div className="text-center py-12">
            <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              {vendorTypeFilter ? `No ${vendorTypeFilter} vendors found` : 'No vendors yet'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {canCreateVendors 
                ? 'Create your first vendor to get started with contract management.'
                : 'Your organization doesn\'t have any vendors yet.'
              }
            </p>
            {canCreateVendors && (
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="btn-primary"
              >
                Create Your First Vendor
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {vendors.map((vendor) => (
            <div key={vendor.id} className="card hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    {vendor.name}
                  </h3>
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full">
                      {vendor.vendor_type || 'vendor'}
                    </span>
                    {vendor.risk_level && (
                      <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getRiskLevelColor(vendor.risk_level)}`}>
                        {vendor.risk_level} risk
                      </span>
                    )}
                  </div>
                </div>
                {canDeleteVendors && (
                  <div className="relative">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setRenameVendorState({ id: vendor.id, name: vendor.name })}
                        className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                        title="Rename vendor"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteVendor({ id: vendor.id, name: vendor.name })}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                        title="Delete vendor"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {vendor.description && (
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-2">
                  {vendor.description}
                </p>
              )}

              <div className="space-y-2 text-sm text-gray-500 dark:text-gray-400">
                <div className="flex items-center">
                  <FileText className="w-4 h-4 mr-2" />
                  <span>{vendor.document_count || 0} contracts</span>
                </div>
                {vendor.contact_email && (
                  <div className="flex items-center">
                    <User className="w-4 h-4 mr-2" />
                    <span>{vendor.contact_email}</span>
                  </div>
                )}
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-2" />
                  <span>Added {formatDate(vendor.created_at)}</span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button className="w-full btn-secondary" onClick={() => navigate(`/vendors/${vendor.id}`)}>
                  View Contracts
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination info */}
      {vendorsData && vendorsData.total > 0 && (
        <div className="text-center text-sm text-gray-500 dark:text-gray-400">
          Showing {vendors.length} of {vendorsData.total} vendors
        </div>
      )}

      {/* Modals */}
      <CreateVendorModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateVendor}
        isLoading={isCreating}
      />

      <DeleteConfirmModal
        isOpen={!!deleteVendor}
        onClose={() => setDeleteVendor(null)}
        onConfirm={handleDeleteVendor}
        vendorName={deleteVendor?.name || ''}
        isLoading={isDeleting}
      />

      {/* Rename Modal */}
      {renameVendorState && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">Rename Vendor</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">New Name</label>
              <input
                type="text"
                value={renameVendorState.name}
                onChange={(e) => setRenameVendorState({ ...renameVendorState, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                placeholder="Enter new vendor name"
                disabled={isRenaming}
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setRenameVendorState(null)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                disabled={isRenaming}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRenameVendor}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                disabled={isRenaming || !renameVendorState.name.trim()}
              >
                {isRenaming ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span className="ml-2">Renaming...</span>
                  </>
                ) : (
                  'Rename'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};